import type { CalendarEvent } from '../stores/calendar'
import { addDaysToKey, dayOfWeek, daysBetween } from './writingDays'

// Repeating calendar events. A series is one stored event with a rule; its
// occurrences are worked out for the days being looked at and never stored.
// An occurrence that is changed or skipped gets its own row, an exception,
// whose id is fixed by the series and the date it replaces. So two devices
// editing the same Tuesday write the same row, and edits to different
// Tuesdays never overwrite each other. Rules are the everyday part of
// iCalendar's RRULE, so .ics import and export only have to translate.
// Kept free of the stores so the rules can be tested on their own.

export type RepeatFreq = 'daily' | 'weekly' | 'monthly' | 'yearly'

export type RepeatEnd =
  | { kind: 'never' }
  /** The last day an occurrence may start on, 'YYYY-MM-DD'. */
  | { kind: 'until'; date: string }
  | { kind: 'count'; count: number }

export interface EventRepeat {
  freq: RepeatFreq
  /** Every N days, weeks, months, or years. */
  interval: number
  /** Weekly: the weekdays it falls on (0 = Sunday). */
  weekdays: number[]
  /** Monthly: on a day of the month, or on a weekday of it ("the 2nd Tuesday"). */
  monthly: 'date' | 'weekday'
  /**
   * Monthly by date, and yearly: the day of the month. Kept apart from the
   * start date so a month too short for it (it lands on the last day then)
   * doesn't change it for good.
   */
  monthDay: number
  /** Monthly by weekday: which one, 1–5, or -1 for the last. The weekday is the start date's. */
  nth: number
  end: RepeatEnd
}

const FREQS: readonly RepeatFreq[] = ['daily', 'weekly', 'monthly', 'yearly']
const DAY = /^\d{4}-\d{2}-\d{2}$/
/** Most occurrences one call works out, whatever the range asked for. */
const MAX_OCCURRENCES = 5000

const pad = (n: number) => String(n).padStart(2, '0')
const whole = (value: unknown, min: number, max: number, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(value))) : fallback

const dayOf = (stored: string) => stored.slice(0, 10)
const dayOfMonth = (day: string) => Number(day.slice(8, 10))

/** The id of the exception that replaces a series' occurrence on `occurrence`. */
export function exceptionId(seriesId: string, occurrence: string): string {
  return `${seriesId}~${occurrence}`
}

function parseEnd(raw: any): RepeatEnd {
  if (raw?.kind === 'until' && typeof raw.date === 'string' && DAY.test(raw.date)) return { kind: 'until', date: raw.date }
  if (raw?.kind === 'count') return { kind: 'count', count: whole(raw.count, 1, 9999, 1) }
  return { kind: 'never' }
}

/** A rule read from storage, its missing parts taken from the start date; null when it isn't one. */
export function parseRepeat(raw: any, start: string): EventRepeat | null {
  if (!raw || typeof raw !== 'object' || !FREQS.includes(raw.freq)) return null
  const startDay = dayOf(start)
  const weekdays = Array.isArray(raw.weekdays)
    ? [...new Set<number>(raw.weekdays.filter((d: unknown) => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6))].sort()
    : []
  return {
    freq: raw.freq,
    interval: whole(raw.interval, 1, 999, 1),
    weekdays: weekdays.length ? weekdays : [dayOfWeek(startDay)],
    monthly: raw.monthly === 'weekday' ? 'weekday' : 'date',
    monthDay: whole(raw.monthDay, 1, 31, dayOfMonth(startDay)),
    nth: raw.nth === -1 ? -1 : whole(raw.nth, 1, 5, Math.ceil(dayOfMonth(startDay) / 7)),
    end: parseEnd(raw.end),
  }
}

/** A rule for an event starting on `start`, with the details filled in from that date. */
export function repeatFor(start: string, patch: Partial<EventRepeat> & { freq: RepeatFreq }): EventRepeat {
  return parseRepeat(patch, start)!
}

// ── Working out dates ───────────────────────────────────────────────────────

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

/** The Nth of a month (1-based month, any count past 12), or its last day when the month is shorter. */
function monthDate(year: number, month: number, day: number): string {
  const y = year + Math.floor((month - 1) / 12)
  const m = ((month - 1) % 12 + 12) % 12 + 1
  return `${y}-${pad(m)}-${pad(Math.min(day, daysInMonth(y, m)))}`
}

/** The nth (1–5, or -1 for last) `weekday` of a month; a missing 5th is the last. */
function nthWeekday(year: number, month: number, weekday: number, nth: number): string {
  const y = year + Math.floor((month - 1) / 12)
  const m = ((month - 1) % 12 + 12) % 12 + 1
  const length = daysInMonth(y, m)
  const lastDow = new Date(y, m - 1, length, 12).getDay()
  const last = length - ((lastDow - weekday + 7) % 7)
  if (nth === -1) return `${y}-${pad(m)}-${pad(last)}`
  const firstDow = new Date(y, m - 1, 1, 12).getDay()
  const first = 1 + ((weekday - firstDow + 7) % 7)
  return `${y}-${pad(m)}-${pad(Math.min(first + (nth - 1) * 7, last))}`
}

function monthsBetween(from: string, to: string): number {
  return (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + Number(to.slice(5, 7)) - Number(from.slice(5, 7))
}

/** Monday of the week holding `day`; weeks are counted from Monday, as iCalendar does. */
function mondayOf(day: string): string {
  return addDaysToKey(day, -((dayOfWeek(day) + 6) % 7))
}

/**
 * The series' occurrences in order, each with its place in the series (for
 * "after N times"), starting at or a little before `near` rather than from
 * the beginning. Only dates on or after the start date count.
 */
function* walk(repeat: EventRepeat, startDay: string, near: string): Generator<{ date: string; index: number }> {
  const lo = near > startDay ? near : startDay
  const { interval } = repeat
  if (repeat.freq === 'weekly') {
    const days = [...repeat.weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
    const firstMonday = mondayOf(startDay)
    const firstWeekCount = days.filter((d) => addDaysToKey(firstMonday, (d + 6) % 7) >= startDay).length
    let week = Math.max(0, Math.floor(daysBetween(firstMonday, mondayOf(lo)) / 7 / interval))
    for (;;) {
      const monday = addDaysToKey(firstMonday, week * interval * 7)
      let index = week === 0 ? 0 : firstWeekCount + (week - 1) * days.length
      for (const d of days) {
        const date = addDaysToKey(monday, (d + 6) % 7)
        if (date < startDay) continue
        yield { date, index: index++ }
      }
      week += 1
    }
  }
  const [year, month] = startDay.split('-').map(Number)
  const weekday = dayOfWeek(startDay)
  const at = (k: number): string => {
    switch (repeat.freq) {
      case 'daily': return addDaysToKey(startDay, k * interval)
      case 'monthly': return repeat.monthly === 'weekday'
        ? nthWeekday(year, month + k * interval, weekday, repeat.nth)
        : monthDate(year, month + k * interval, repeat.monthDay)
      default: return monthDate(year + k * interval, month, repeat.monthDay)
    }
  }
  // A day of the month set before the start date skips the first month.
  const skipsFirst = at(0) < startDay ? 1 : 0
  const step = repeat.freq === 'daily' ? interval : repeat.freq === 'monthly' ? interval : interval * 12
  const gap = repeat.freq === 'daily' ? daysBetween(startDay, lo) : monthsBetween(startDay, lo)
  for (let k = Math.max(0, Math.floor(gap / step) - 1); ; k++) {
    const date = at(k)
    if (date < startDay) continue
    yield { date, index: k - skipsFirst }
  }
}

/** Days, 'YYYY-MM-DD', on which the series has an occurrence, from `from` up to but not including `to`. */
export function occurrenceDates(repeat: EventRepeat, start: string, from: string, to: string): string[] {
  const out: string[] = []
  const { end } = repeat
  let guard = 0
  for (const { date, index } of walk(repeat, dayOf(start), from)) {
    if (date >= to || ++guard > MAX_OCCURRENCES) break
    if (end.kind === 'until' && date > end.date) break
    if (end.kind === 'count' && index >= end.count) break
    if (date >= from) out.push(date)
  }
  return out
}

export function isOccurrence(repeat: EventRepeat, start: string, day: string): boolean {
  return occurrenceDates(repeat, start, day, addDaysToKey(day, 1)).length > 0
}

/** The first occurrence on or after `from`, or null when the series has ended. */
export function nextOccurrence(repeat: EventRepeat, start: string, from: string): string | null {
  // Ten years ahead is far enough for any rule a person sets.
  for (let lo = from, years = 0; years < 10; years++, lo = addDaysToKey(lo, 366)) {
    const found = occurrenceDates(repeat, start, lo, addDaysToKey(lo, 366))
    if (found.length) return found[0]
  }
  return null
}

// ── Times ───────────────────────────────────────────────────────────────────
// Floating local times are worked out on a clock without daylight saving, so
// a 9:00 event stays at 9:00 and a two-hour one stays two hours.

function wallMinutes(stored: string): number {
  const [datePart, timePart = '00:00'] = stored.split('T')
  const [y, mo, d] = datePart.split('-').map(Number)
  const [h, mi] = timePart.split(':').map(Number)
  return Date.UTC(y, mo - 1, d, h, mi) / 60_000
}

function fromWallMinutes(minutes: number, allDay: boolean): string {
  const date = new Date(minutes * 60_000)
  const day = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
  return allDay ? day : `${day}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
}

/** The day after an event ends, so a range check can treat every event alike. */
function endDayExclusive(start: string, end: string | null, allDay: boolean): string {
  if (allDay) return end ? dayOf(end) : addDaysToKey(dayOf(start), 1)
  if (!end) return addDaysToKey(dayOf(start), 1)
  // A timed event that ends at midnight doesn't reach into the next day.
  return end.endsWith('T00:00') ? dayOf(end) : addDaysToKey(dayOf(end), 1)
}

function overlaps(start: string, end: string | null, allDay: boolean, from: string, to: string): boolean {
  return dayOf(start) < to && endDayExclusive(start, end, allDay) > from
}

/** The series moved to `day`: the same time of day and the same length. */
export function occurrenceEvent(series: CalendarEvent, day: string): CalendarEvent {
  const shift = daysBetween(dayOf(series.start), day) * 1440
  return {
    ...series,
    start: fromWallMinutes(wallMinutes(series.start) + shift, series.allDay),
    end: series.end ? fromWallMinutes(wallMinutes(series.end) + shift, series.allDay) : null,
  }
}

// ── Expanding ───────────────────────────────────────────────────────────────

export interface Occurrence {
  /** The event as it shows: the series' details on that day, or the exception that replaces them. */
  event: CalendarEvent
  /** The series it belongs to, or null for an event that doesn't repeat. */
  seriesId: string | null
  /** The day the series planned it for, which names its exception; null when it doesn't repeat. */
  occurrence: string | null
}

/** Live exceptions by id: their series still exists and still has that occurrence. */
function liveExceptions(events: CalendarEvent[]): Map<string, CalendarEvent> {
  const series = new Map(events.filter((e) => e.repeat && !e.seriesId).map((e) => [e.id, e]))
  const out = new Map<string, CalendarEvent>()
  for (const event of events) {
    if (!event.seriesId || !event.occurrence) continue
    const parent = series.get(event.seriesId)
    if (parent && isOccurrence(parent.repeat!, parent.start, event.occurrence)) {
      out.set(exceptionId(event.seriesId, event.occurrence), event)
    }
  }
  return out
}

/**
 * Everything that shows from `from` up to but not including `to`: events that
 * don't repeat, each series' occurrences, and the exceptions in their place.
 * Skipped occurrences are left out. Sorted by start.
 */
export function expandEvents(events: CalendarEvent[], from: string, to: string): Occurrence[] {
  const exceptions = liveExceptions(events)
  const out: Occurrence[] = []
  for (const event of events) {
    if (event.seriesId) continue
    if (!event.repeat) {
      if (overlaps(event.start, event.end, event.allDay, from, to)) out.push({ event, seriesId: null, occurrence: null })
      continue
    }
    // Look back far enough to catch a multi-day occurrence that began earlier.
    const span = daysBetween(dayOf(event.start), endDayExclusive(event.start, event.end, event.allDay)) - 1
    for (const day of occurrenceDates(event.repeat, event.start, addDaysToKey(from, -span), to)) {
      if (exceptions.has(exceptionId(event.id, day))) continue
      const shown = occurrenceEvent(event, day)
      if (overlaps(shown.start, shown.end, shown.allDay, from, to)) out.push({ event: shown, seriesId: event.id, occurrence: day })
    }
  }
  // An exception shows where it now is, which may be outside its series' range.
  for (const exception of exceptions.values()) {
    if (exception.canceled || !overlaps(exception.start, exception.end, exception.allDay, from, to)) continue
    out.push({ event: exception, seriesId: exception.seriesId, occurrence: exception.occurrence })
  }
  return out.sort((a, b) => (a.event.start < b.event.start ? -1 : a.event.start > b.event.start ? 1 : 0))
}

// ── Changing a series ───────────────────────────────────────────────────────

/** The fields an exception shares with its series until it is given its own. */
const SHARED = ['title', 'color', 'notes', 'linkedId', 'writing'] as const

/** An exception that replaces the series' occurrence on `occurrence` with `changed`. */
export function makeException(series: CalendarEvent, occurrence: string, changed: CalendarEvent, canceled = false): CalendarEvent {
  return {
    ...changed,
    id: exceptionId(series.id, occurrence),
    repeat: null,
    seriesId: series.id,
    occurrence,
    canceled,
  }
}

/**
 * The series' rule after its start moved from `fromDay` to `toDay`, so it
 * keeps falling where the move put it: weekdays shift with it, and the day
 * of the month or which weekday of it follows the new date.
 */
export function reanchorRepeat(repeat: EventRepeat, fromDay: string, toDay: string): EventRepeat {
  const delta = daysBetween(fromDay, toDay)
  if (!delta) return repeat
  return {
    ...repeat,
    weekdays: [...new Set(repeat.weekdays.map((d) => (((d + delta) % 7) + 7) % 7))].sort(),
    monthDay: dayOfMonth(toDay),
    nth: repeat.nth === -1 ? -1 : Math.ceil(dayOfMonth(toDay) / 7),
  }
}

/**
 * The series after one of its occurrences was edited for "All events": the
 * new details, and its start moved by as much as the occurrence was. When
 * the rule is unchanged it follows the move (see reanchorRepeat).
 */
export function seriesFromEdit(series: CalendarEvent, occurrence: string, edited: CalendarEvent): CalendarEvent {
  const planned = occurrenceEvent(series, occurrence)
  const shift = wallMinutes(edited.start) - wallMinutes(planned.start)
  const start = fromWallMinutes(wallMinutes(series.start) + shift, edited.allDay)
  const end = edited.end ? fromWallMinutes(wallMinutes(start) + wallMinutes(edited.end) - wallMinutes(edited.start), edited.allDay) : null
  const sameRule = JSON.stringify(edited.repeat) === JSON.stringify(series.repeat)
  return {
    ...edited,
    id: series.id,
    start,
    end,
    repeat: edited.repeat && sameRule ? reanchorRepeat(edited.repeat, dayOf(series.start), dayOf(start)) : edited.repeat,
    seriesId: null,
    occurrence: null,
    canceled: false,
  }
}

export interface RowChanges {
  save: CalendarEvent[]
  remove: string[]
}

/**
 * The series' exceptions after the series itself changed from `before` to
 * `after`. Whatever an exception hadn't changed follows the series: a shared
 * detail still equal to the old one takes the new one, and an exception
 * still at its planned time moves with the series. Exceptions whose day the
 * new rule no longer has are removed.
 */
export function followSeries(before: CalendarEvent, after: CalendarEvent, exceptions: CalendarEvent[]): RowChanges {
  const changes: RowChanges = { save: [], remove: [] }
  const delta = daysBetween(dayOf(before.start), dayOf(after.start))
  for (const exception of exceptions) {
    if (exception.seriesId !== before.id || !exception.occurrence) continue
    const occurrence = addDaysToKey(exception.occurrence, delta)
    if (!after.repeat || !isOccurrence(after.repeat, after.start, occurrence)) {
      changes.remove.push(exception.id)
      continue
    }
    const next: CalendarEvent = { ...exception, id: exceptionId(after.id, occurrence), seriesId: after.id, occurrence }
    for (const field of SHARED) {
      if (exception[field] === before[field]) (next as any)[field] = after[field]
    }
    const planned = occurrenceEvent(before, exception.occurrence)
    if (exception.start === planned.start && exception.end === planned.end && exception.allDay === planned.allDay) {
      const moved = occurrenceEvent(after, occurrence)
      Object.assign(next, { start: moved.start, end: moved.end, allDay: moved.allDay })
    }
    if (next.id !== exception.id) changes.remove.push(exception.id)
    if (JSON.stringify(next) !== JSON.stringify(exception)) changes.save.push(next)
  }
  return changes
}

/**
 * "This and following": the series stops before `occurrence`, and a new one
 * (`newId`) carries on from it with the same rule. `ended` is null when
 * nothing came before, which makes this the whole series. Exceptions from
 * that day on move to the new series.
 */
export function splitSeries(series: CalendarEvent, occurrence: string, newId: string, exceptions: CalendarEvent[]): {
  ended: CalendarEvent | null
  next: CalendarEvent
  changes: RowChanges
} {
  const repeat = series.repeat!
  const before = occurrenceDates(repeat, series.start, dayOf(series.start), occurrence).length
  const ended = before ? { ...series, repeat: { ...repeat, end: { kind: 'until', date: addDaysToKey(occurrence, -1) } as RepeatEnd } } : null
  const end: RepeatEnd = repeat.end.kind === 'count' ? { kind: 'count', count: Math.max(1, repeat.end.count - before) } : repeat.end
  const next: CalendarEvent = { ...occurrenceEvent(series, occurrence), id: newId, repeat: { ...repeat, end } }
  const changes: RowChanges = { save: [], remove: [] }
  for (const exception of exceptions) {
    if (exception.seriesId !== series.id || !exception.occurrence || exception.occurrence < occurrence) continue
    changes.remove.push(exception.id)
    changes.save.push({ ...exception, id: exceptionId(newId, exception.occurrence), seriesId: newId })
  }
  return { ended, next, changes }
}
